import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useContactClientForRequest } from '../../hooks/useMessaging';
import './ContactClientModal.css';

/**
 * Contact Client Modal
 * Allows MarkoTM attorneys to compose a message to the client
 * based on an external attorney's internal request.
 */
function ContactClientModal({ isOpen, onClose, request }) {
  const { t } = useTranslation('attorney');
  const contactClient = useContactClientForRequest();

  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    urgency: 'medium',
    items: []
  });

  const [newItem, setNewItem] = useState({ type: 'text', label: '', required: true });

  // Pre-populate from request data
  useEffect(() => {
    if (request) {
      setFormData({
        subject: request.title || '',
        body: request.message || '',
        urgency: request.urgency || 'medium',
        items: request.requestData?.items || []
      });
    }
  }, [request]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddItem = () => {
    if (!newItem.label.trim()) return;

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...newItem, id: `item_${Date.now()}` }]
    }));
    setNewItem({ type: 'text', label: '', required: true });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject || !formData.body) {
      alert('Please fill in subject and message');
      return;
    }

    try {
      await contactClient.mutateAsync({
        requestId: request.id,
        messageData: {
          subject: formData.subject,
          body: formData.body,
          urgency: formData.urgency,
          items: formData.items
        }
      });

      onClose();
    } catch (error) {
      console.error('Error contacting client:', error);
      alert(`Error sending message: ${error.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content contact-client-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('attorney.contactClient.title', 'Contact Client')}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Original Request Info */}
            <div className="original-request-info">
              <div className="info-row">
                <label>{t('attorney.contactClient.originalRequest', 'Original Request From')}:</label>
                <span>
                  {request.metadata?.requestingAttorneyName}
                  {request.metadata?.requestingFirmName && ` (${request.metadata.requestingFirmName})`}
                </span>
              </div>
              <div className="info-row">
                <label>{t('attorney.contactClient.application', 'Application')}:</label>
                <span>{request.applicationId}</span>
              </div>
              {request.metadata?.clientName && (
                <div className="info-row">
                  <label>{t('attorney.contactClient.client', 'Client')}:</label>
                  <span>{request.metadata.clientName}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="subject">
                {t('attorney.contactClient.subject', 'Subject')} *
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Information needed for your trademark application"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="body">
                {t('attorney.contactClient.message', 'Message to Client')} *
              </label>
              <textarea
                id="body"
                name="body"
                value={formData.body}
                onChange={handleChange}
                rows={6}
                placeholder="Dear Client, we need the following information..."
                required
              />
              <p className="form-hint">
                {t('attorney.contactClient.hint', 'This message will be sent to the client on behalf of MarkoTM. The external attorney will receive the response once the client replies.')}
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="urgency">
                {t('attorney.contactClient.urgency', 'Urgency')}
              </label>
              <select
                id="urgency"
                name="urgency"
                value={formData.urgency}
                onChange={handleChange}
              >
                <option value="low">{t('attorney.urgency.low', 'Low')}</option>
                <option value="medium">{t('attorney.urgency.medium', 'Medium')}</option>
                <option value="high">{t('attorney.urgency.high', 'High')}</option>
                <option value="urgent">{t('attorney.urgency.urgent', 'Urgent')}</option>
              </select>
            </div>

            {/* Request Items */}
            <div className="form-section">
              <h3>{t('attorney.contactClient.requestItems', 'Request Items')}</h3>
              <p className="form-hint">
                {t('attorney.contactClient.itemsHint', 'Add specific items for the client to provide')}
              </p>

              {formData.items.length > 0 && (
                <ul className="items-list">
                  {formData.items.map((item, index) => (
                    <li key={index} className="item-row">
                      <span className="item-type">[{item.type}]</span>
                      <span className="item-label">{item.label}</span>
                      {item.required && <span className="item-required">*</span>}
                      <button
                        type="button"
                        className="item-remove"
                        onClick={() => handleRemoveItem(index)}
                      >
                        &times;
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="add-item-form">
                <select
                  value={newItem.type}
                  onChange={(e) => setNewItem(prev => ({ ...prev, type: e.target.value }))}
                >
                  <option value="text">{t('attorney.itemType.text', 'Text Input')}</option>
                  <option value="file_upload">{t('attorney.itemType.file', 'File Upload')}</option>
                  <option value="date">{t('attorney.itemType.date', 'Date')}</option>
                  <option value="select">{t('attorney.itemType.select', 'Selection')}</option>
                </select>
                <input
                  type="text"
                  value={newItem.label}
                  onChange={(e) => setNewItem(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Item label (e.g., 'Business address')"
                />
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newItem.required}
                    onChange={(e) => setNewItem(prev => ({ ...prev, required: e.target.checked }))}
                  />
                  Required
                </label>
                <button type="button" className="btn btn-secondary" onClick={handleAddItem}>
                  + Add
                </button>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={contactClient.isPending}
            >
              {contactClient.isPending
                ? t('common.sending', 'Sending...')
                : t('attorney.contactClient.send', 'Send to Client')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactClientModal;
